import { useEffect, useState } from 'react'

import {
  Modal,
  Textarea,
  Input,
  CloseButton,
  Divider,
  TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useEmailModalContext } from 'src/context/EmailModalContext'
import { SEND_CUSTOM_EMAIL } from 'src/graphql/email.graphql'
import useUploadDocument from 'src/hooks/useUploadDocument/useUploadDocument'

import BodyTextArea from './components/BodyTextArea'
import EmailModalFooter from './components/EmailModalFooter'

const schema = z.object({
  to: z.string().min(1, 'At least one recipient is required'),
  cc: z.string().optional(),
  bcc: z.string().optional(),
  subject: z
    .string()
    .min(1, 'Subject is required')
    .max(200, 'Subject cannot exceed 200 characters'),
  body: z
    .string()
    .min(1, 'Email body is required')
    .max(10000, 'Body cannot exceed 10000 characters'),
})

const WriteEmailModal = () => {
  const {
    opened: isOpen,
    handleClose: onClose,
    applicantId,
    defaultTo = '',
    defaultSubject = '',
    defaultBody = '',
    onSuccess,
  } = useEmailModalContext()

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [templateAttachments, setTemplateAttachments] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<{
    subject: string
    body: string
    attachments?: unknown[]
    emailTemplateId?: string
  } | null>(null)
  const [selectedTemplateState, setSelectedTemplateState] = useState<{
    title: string | null
    emailTemplateId: string | null
  } | null>(null)
  const [isCCVisible, setIsCCVisible] = useState(false)
  const [isBCCVisible, setIsBCCVisible] = useState(false)

  const MAX_ATTACHMENT_SIZE = 9.5 * 1024 * 1024 // 9.5MB (500K margin under the 10MB backend limit)

  const handleFileDrop = (files: File[]) => {
    const currentSize = selectedFiles.reduce((acc, file) => acc + file.size, 0)
    const newSize = files.reduce((acc, file) => acc + file.size, 0)
    if (currentSize + newSize > MAX_ATTACHMENT_SIZE) {
      toast('Attachment size limit reached (10MB)', 'warning')
      return
    }
    setSelectedFiles([...selectedFiles, ...files])
  }
  const { handleCreateDocuments, isUploading } = useUploadDocument(
    ['GET_DOCUMENTS'],
    applicantId
  )
  const [sendCustomEmail, { loading: emailLoading }] = useMutation(
    SEND_CUSTOM_EMAIL,
    {
      // Refresh any mounted activity-log thread (e.g. the sidebar Email tab)
      // so the just-sent email appears as a bubble without a remount.
      refetchQueries: ['GetActivityLogs'],
      onCompleted: () => {
        // sendEmailWithEmployeeId always returns sent: true or throws, so
        // onCompleted only fires on success — no need to check sent flag.
        toast('Email sent successfully!', 'success')
        setSelectedTemplateState(null)
        // The modal is mounted once in DashboardLayout and never unmounts, so
        // template state has to be cleared here. Otherwise the next compose
        // silently reuses the previous emailTemplateId (and its attachments)
        // while showing no template chip.
        setSelectedTemplate(null)
        setTemplateAttachments([])
        onSuccess?.()
        onClose()
        setSelectedFiles([])
        setIsSubmitting(false)
      },
      onError: (error) => {
        // Try multiple paths to support both dev and production environments
        const errorMessage =
          error.graphQLErrors?.[0]?.extensions?.originalError?.message || // Dev environment (detailed errors)
          error.graphQLErrors?.[0]?.message || // Production environment (masked errors)
          error.message || // Final fallback
          'Unable to send email' // Default message
        if (errorMessage.includes('needs to be reconnected')) {
          toast(
            <>
              Your email account needs to be reconnected.{' '}
              <a href="/settings?tab=users" className="font-bold underline">
                Go to Settings
              </a>
            </>,
            'error',
            { errorMessage } // preserve actual message in Datadog since text is JSX
          )
        } else {
          toast(errorMessage, 'error')
        }
        setIsSubmitting(false)
      },
    }
  )

  // Combined loading state
  const loading = isSubmitting || emailLoading || isUploading

  const form = useForm({
    initialValues: {
      to: defaultTo,
      cc: '',
      bcc: '',
      subject: defaultSubject,
      body: defaultBody,
    },
    validate: zodResolver(schema),
    validateInputOnChange: true,
  })

  useEffect(() => {
    form.setValues({
      to: defaultTo,
      subject: defaultSubject,
      body: defaultBody,
    })
  }, [defaultTo, defaultSubject, defaultBody])

  // Helper function to parse email string to array and validate
  const parseAndValidateEmails = (emailString: string): string[] => {
    if (!emailString.trim()) return []

    const emails = emailString
      .split(/[,;\s]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0)

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const invalidEmails = emails.filter((email) => !emailRegex.test(email))

    if (invalidEmails.length > 0) {
      throw new Error(`Invalid email addresses: ${invalidEmails.join(', ')}`)
    }

    return emails
  }

  const onFormSubmit = async (values) => {
    try {
      setIsSubmitting(true)

      const toEmails = parseAndValidateEmails(values.to)
      const ccEmails = parseAndValidateEmails(values.cc || '')
      const bccEmails = parseAndValidateEmails(values.bcc || '')
      if (toEmails.length === 0) {
        toast('At least one recipient is required', 'error')
        setIsSubmitting(false)
        return
      }

      const send = (docs: string[] = []) =>
        sendCustomEmail({
          variables: {
            input: {
              applicantId,
              to: toEmails,
              cc: ccEmails.length > 0 ? ccEmails : undefined,
              bcc: bccEmails.length > 0 ? bccEmails : undefined,
              subject: values.subject,
              body: values.body,
              attachments: selectedTemplate?.emailTemplateId
                ? undefined
                : docs.length > 0
                ? docs
                : undefined,
              emailTemplateId: selectedTemplate?.emailTemplateId,
            },
          },
        })

      if (selectedFiles.length > 0 && !selectedTemplate?.emailTemplateId) {
        handleCreateDocuments(selectedFiles, (docs) => {
          send(docs)
        })
      } else {
        await send()
      }
    } catch (error) {
      toast(error.message, 'error')
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    form.reset()
    setSelectedTemplateState(null)
    setSelectedFiles([])
    setTemplateAttachments([])
    setIsSubmitting(false)
    setSelectedTemplate(null)
    onClose()
  }

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title="Send Email"
      closeOnEscape
      closeOnClickOutside
      stackId="write-email-modal"
      transitionProps={{
        transition: 'slide-up',
        duration: 300,
        timingFunction: 'ease',
      }}
      overlayProps={{
        backgroundOpacity: 0.05,
      }}
      classNames={{
        title: 'font-semibold',
        body: 'flex flex-col gap-2 overflow-y-auto h-full',
        content:
          'fixed bottom-1.5 right-1.5 m-0 transition-all duration-300 rounded-2xl shadow-lg max-h-[100vh] w-[45vw]',
      }}
    >
      <form
        className="flex flex-1 flex-col gap-4"
        onSubmit={form.onSubmit(onFormSubmit)}
      >
        <div className="flex h-full w-full flex-col gap-2">
          <div className="flex w-full flex-row items-baseline gap-2">
            <Input.Label>To:</Input.Label>
            <Textarea
              variant="unstyled"
              className="flex-1"
              placeholder="Enter recipient emails (separated by commas)..."
              required
              autosize
              maxRows={2}
              readOnly
              {...form.getInputProps('to')}
              classNames={{
                label: 'text-sm font-medium text-doubleNickel-gray-700',
              }}
            />
            <DNButton
              variant="transparent"
              onClick={() => {
                form.setFieldValue('cc', '')
                setIsCCVisible(!isCCVisible)
              }}
            >
              CC
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={() => setIsBCCVisible(!isBCCVisible)}
            >
              BCC
            </DNButton>
          </div>
          <Divider />
          {isCCVisible && (
            <>
              <div className="flex w-full flex-row items-baseline gap-2 ">
                <Input.Label>CC:</Input.Label>
                <TextInput
                  placeholder="Enter CC emails (separated by commas)..."
                  variant="unstyled"
                  className="w-full flex-1"
                  {...form.getInputProps('cc')}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              </div>
              <Divider />
            </>
          )}

          {isBCCVisible && (
            <>
              <div className="flex w-full flex-row items-baseline gap-2 ">
                <Input.Label>BCC:</Input.Label>
                <TextInput
                  placeholder="Enter BCC emails (separated by commas)..."
                  variant="unstyled"
                  className="w-full flex-1"
                  {...form.getInputProps('bcc')}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              </div>
              <Divider />
            </>
          )}

          <div className="flex w-full flex-row items-center gap-2">
            <Textarea
              placeholder="Subject"
              variant="unstyled"
              classNames={{
                root: 'mt-1 flex-1',
                label: 'text-sm font-medium text-doubleNickel-gray-700',
              }}
              required
              autosize
              maxRows={2}
              maxLength={200}
              value={form.values.subject as string}
              onChange={(e) => {
                form.setFieldValue('subject', e.target.value)
              }}
            />
          </div>
          <Divider />
          <BodyTextArea form={form} onEditorReady={() => {}} />

          {(selectedFiles.length > 0 || templateAttachments.length > 0) && (
            <div className="flex flex-col gap-1">
              {/* Template attachments (read-only) */}
              {templateAttachments.map((attachment, index) => (
                <div
                  key={`template-${index}`}
                  className="flex items-center justify-between rounded-sm border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 p-1 text-xs"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-doubleNickel-gray-600">
                      📎
                    </span>
                    <span className="text-left text-doubleNickel-gray-700">
                      {attachment.fileName} (from template)
                    </span>
                  </div>
                  <span className="text-xs text-doubleNickel-gray-500">
                    Read-only
                  </span>
                </div>
              ))}

              {/* User selected files (only show if no template is selected) */}
              {!selectedTemplate?.emailTemplateId &&
                selectedFiles.map((file, index) => (
                  <div
                    key={`file-${index}`}
                    className="flex items-center justify-between rounded-sm p-1 text-xs hover:bg-doubleNickel-brand-25"
                  >
                    <button
                      type="button"
                      className="cursor-pointer text-left underline"
                      onClick={() => window.open(URL.createObjectURL(file))}
                    >
                      {file.name}
                    </button>
                    <CloseButton
                      size="xs"
                      onClick={() =>
                        setSelectedFiles(
                          selectedFiles.filter((_, i) => i !== index)
                        )
                      }
                    />
                  </div>
                ))}
            </div>
          )}
        </div>
        <EmailModalFooter
          applicantId={applicantId}
          selectedTemplateState={selectedTemplateState}
          onTemplateStateChange={setSelectedTemplateState}
          onFileDrop={handleFileDrop}
          handleClose={handleClose}
          loading={loading}
          form={form}
          hasTemplate={!!selectedTemplate?.emailTemplateId}
          onTemplateSelect={(template) => {
            setSelectedTemplate(template)
            setTemplateAttachments(template?.attachments ?? [])
          }}
        />
      </form>
    </Modal>
  )
}

export default WriteEmailModal
